<template>
  <div
    v-if="quiz"
    :style="cssVariables"
    :class="wrappClass"
    class="quick-poll"
  >
    <ClientOnly>
      <h4 class="quick-poll__question">{{ quiz.question }}</h4>
      <div class="quick-poll__answers">
        <QuickPollBtn
          v-for="answer in quiz.answers"
          :key="answer.id"
          :style="isAnswered ? `width: ${countStatistic(answer.id)}%` : {}"
          :text="answer.text"
          :is-answered="isAnswered"
          :statistics="countStatistic(answer.id)"
          :answered-delay="answeredDelay"
          :is-selected="checkSelectedAnswer(answer.id)"
          :disabled="isAnswered"
          @click-btn="clickAnswer(answer.id)"
        />
      </div>
      <BaseTransitionAnimations name="fade">
        <div
          v-if="answeredDelay"
          class="quick-poll__article"
        >
          <img
            class="quick-poll__article-image"
            :srcset="`${getCoverSrc(false)}, ${getCoverSrc(true)} 2x`"
            :src="getCoverSrc(false)"
            width="288"
            height="360"
          />
          <div class="article-action">
            <div class="article-action__title">{{ quiz.article.title }}</div>
            <a
              class="article-action__btn"
              :href="quiz.article.url"
              target="_blank"
              @click="onClickArticle(quiz.article.url)"
            >
              Читать статью
            </a>
          </div>
        </div>
      </BaseTransitionAnimations>
      <TestPixel
        v-if="quiz.testPixel"
        :pixel-link="quiz.testPixel"
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { parseData } from '@devhacker/shared/utils/parseData';
  import QuickPollBtn from './QuickPollBtn.vue';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import BaseTransitionAnimations from '@/components/BaseComponents/BaseTransitionAnimation.vue';
  import { QUIZ_API_URL } from '@/constants/config';
  import TestPixel from '@/components/TestPixel.vue';
  import type {
    TQuickPollData,
    IContentQuickPoll,
    TQuickPoll,
  } from '~/types/components/BaseQuickPoll';
  import type { TImageSize } from '~/types/common';
  import { getQuickPollSchema, getQuickPollStatisticsSchema } from '~/schemas/quizPollSchema';

  const getCover = ({ width }: { width: number }): boolean => width === 640;
  const getCoverRetina = ({ width }: { width: number }): boolean => width === 1280;

  const THEMES = {
    0: {
      background: '#eeeaf9',
      button: '#9989cc',
    },
    1: {
      background: '#f9e6f1',
      button: '#d272aa',
    },
    2: {
      background: '#e9eeff',
      button: '#7c8ecd',
    },
  } as Record<string, Record<string, string>>;

  export default defineNuxtComponent({
    name: 'BaseQuickPoll',

    components: {
      BaseTransitionAnimations,
      TestPixel,
      QuickPollBtn,
    },

    inject: {
      area: {
        default() {
          return () => '';
        },
      },

      // Категория в которой происходит событие
      // Для аналитики
      eventCategory: {
        default() {
          return () => '';
        },
      },

      // Контекст события для аналитики
      eventName: {
        default() {
          return () => 'Коммерческий опрос';
        },
      },
    },

    props: {
      elem: {
        required: true,
        type: Object as PropType<IContentQuickPoll>,
      },
    },

    data(): TQuickPollData {
      return {
        isAnsweredDelay: false,
        isAnswerSent: false,
        quiz: null,
        quizId: null,
        statisticsData: null,
      };
    },

    computed: {
      cssVariables(): Record<string, string> | null {
        if (this.quiz) {
          const quiztheme = this.quiz?.theme;
          return {
            '--primary-color': THEMES[quiztheme]?.background || '#eeeaf9',
            '--secondary-color': THEMES[quiztheme]?.button || '#9989cc',
            '--pb-wrapp': this.isAnswered ? '0' : '40px',
          };
        }
        return null;
      },

      getArea(): string {
        // @ts-expect-error injected functions are not typed in options API
        if (this.area) {
          // @ts-expect-error injected functions are not typed in options API
          return this.area();
        }
        return '';
      },

      wrappClass(): Record<string, boolean> {
        const areas: Array<string> = [
          'above-comments',
          'below-comments',
          'home',
          'after-first-paragraph',
          'post-dynamic',
        ];

        return {
          [`quick-poll--${this.getArea}`]: areas.includes(this.getArea),
        };
      },

      answeredDelay(): boolean | undefined {
        return this.isAnswered && this.isAnsweredDelay;
      },

      isAnswered(): boolean | undefined {
        return Boolean(this.quizLocalStorageData?.state === 'answered' && this.statisticsData);
      },

      quizLocalStorageData(): any {
        if (this.quiz) {
          try {
            const quizData = JSON.parse(localStorage.getItem(`quizId${this.quiz.id}`) || '{}');
            return quizData;
          } catch (err) {
            console.error(err);
          }
        }
        return null;
      },

      imagesHost(): string {
        return QUIZ_API_URL;
      },

      cover(): any {
        return this.quiz?.article.cover;
      },

      defaultCover(): any {
        return this.quiz?.article.default_cover;
      },

      quickPoll(): TQuickPoll {
        return this.elem.attributes;
      },

      currentslug(): string {
        return getSlugFromUrl(this.$route.fullPath);
      },
    },

    async mounted() {
      if (import.meta.client) {
        this.quizId = this.quickPoll.ids[Math.floor(Math.random() * this.quickPoll.ids.length)];
        if (this.quizId) {
          const quizData = await this.$api.quiz.getQuickPoll(this.quizId);
          const parsedData = parseData(quizData?.data, getQuickPollSchema) as any;

          this.quiz = parsedData?.data?.data;

          if (this.quizLocalStorageData?.state === 'answered') {
            const statistics = await this.$api.quiz.getQuickPollStatistics(this.quizId);

            // @ts-expect-error parseData typing is incompatible with runtime schema
            this.statisticsData = parseData(statistics?.data, getQuickPollStatisticsSchema).data;

            setTimeout(() => {
              this.isAnsweredDelay = true;
            }, 500);
          }
        }
      }
    },

    methods: {
      async clickAnswer(quickPollAnswerId: number) {
        if (this.isAnswerSent) {
          return;
        }

        if (this.quiz) {
          try {
            const localData = {
              state: 'answered',
              selectedAnswerId: quickPollAnswerId,
              quizId: this.quiz.id,
            };

            try {
              localStorage.setItem(`quizId${this.quiz.id}`, JSON.stringify(localData));
            } catch (err) {
              console.error(err);
            }

            // * костыль, чтобы vue заново  добавил класс для выбранного ответа и отрендерил
            const copy = { ...this.quiz };
            this.quiz = copy;

            // * Задержка перед анимацией
            setTimeout(() => {
              this.isAnsweredDelay = true;
            }, 500);

            this.isAnswerSent = true;
            await this.$api.quiz.setQuickPollAnswerId(quickPollAnswerId);

            if (this.quizId) {
              const statistics = await this.$api.quiz.getQuickPollStatistics(this.quizId);

              // @ts-expect-error parseData typing is incompatible with runtime schema
              this.statisticsData = parseData(statistics?.data, getQuickPollStatisticsSchema).data;
            }

            const answerText = this.quiz.answers?.find(({ id }) => id === quickPollAnswerId)?.text;

            this.$sendYandexMetrika({
              level1: 'Квизы_Ответы_Быстрый опрос',
              level5: this.quiz.id,
              level6: answerText || String(quickPollAnswerId),
            });
          } catch (err) {
            console.error(err);
          }
        }
      },

      countStatistic(answerId: number): number {
        if (this.statisticsData) {
          const generalClicks = Object.values(this.statisticsData?.clicks).reduce(
            (acc, cur) => (acc += cur),
            0,
          );

          return Math.round(this.statisticsData.clicks[answerId] / (generalClicks / 100));
        }
        return 0;
      },

      checkSelectedAnswer(id: number): boolean | undefined {
        if (this.quizLocalStorageData?.state === 'answered') {
          return this.quizLocalStorageData?.selectedAnswerId === id;
        }
        return false;
      },

      getCoverSrc(isRetina: boolean): string {
        const cover = this.cover;
        const defaultCover = this.defaultCover;
        const imagesHost = this.imagesHost;

        const getCoverSize = (cover: Record<string, TImageSize>) => {
          return Object.keys(cover)
            .map((size) => cover[size])
            .find(isRetina ? getCoverRetina : getCover);
        };

        return cover
          ? imagesHost + getCoverSize(cover.sizes)?.url
          : imagesHost + getCoverSize(defaultCover)?.url;
      },

      onClickArticle(url: string) {
        this.$sendYandexMetrika({
          level1: 'Квизы_Выбор материала_Быстрый опрос',
          level4: url,
          level5: this.quiz?.id ?? this.quizId ?? '',
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  .quick-poll {
    font-family: $text-font--1;
    max-width: 288px;
    background-color: var(--primary-color);
    padding: $spacing-10 $spacing-0 var(--pb-wrapp);
    border-radius: 24px;
    margin: $spacing-0 auto;
    @include tablet {
      border-radius: 32px;
      max-width: 100%;
      padding: $spacing-12 $spacing-6;
    }
    &__question {
      font-family: $headline-font--1;
      font-weight: 400;
      @include headline-small-text;
      text-align: center;
      word-break: break-word;
      padding: $spacing-0 $spacing-6;
      @include tablet {
        padding: $spacing-0;
        @include headline-medium-text;
      }
    }

    &__answers {
      display: flex;
      justify-content: space-between;

      margin: $spacing-6 $spacing-4 $spacing-0;
      @include tablet {
        margin: $spacing-6 $spacing-0 $spacing-0;
      }
      :nth-child(1) {
        border-radius: 32px 0px 0px 32px;
      }
      :nth-child(2) {
        border-radius: 0px 32px 32px 0px;
      }
    }
    &__article {
      width: 100%;
      position: relative;
      margin-top: $spacing-10;
      @include tablet {
        margin-top: $spacing-6;
        border-radius: 32px;
      }
      @include hover {
        .article-action {
          &__btn {
            background: #fff;
            color: #000;
            cursor: pointer;
          }
        }
      }
      &-image {
        display: block;
        height: 360px;
        object-fit: cover;
        width: 100%;
        border-radius: 24px;
        @include tablet {
          height: 100%;
          border-radius: 32px;
        }
      }
      .article-action {
        display: flex;
        flex-direction: column;
        align-items: center;

        position: absolute;
        height: 100%;
        width: 100%;
        padding: $spacing-10 $spacing-6 $spacing-4;
        left: 0;
        top: 0;

        @include tablet {
          padding: $spacing-10 $spacing-6 $spacing-6;
        }
        &__title {
          width: 100%;
          font-weight: 400;
          font-family: $headline-font--1;
          line-height: 34px;
          color: #fff;
          font-size: 20px;
          text-align: left;
          margin-bottom: $spacing-3;
          @include tablet {
            margin-bottom: $spacing-4;
            font-weight: 600;
            font-size: 26px;
            line-height: 36px;
          }
        }
        &__btn {
          text-align: center;
          border-radius: 24px;
          font-family: $text-font--1;
          font-weight: 500;
          @include normalText;
          padding: $spacing-3 $spacing-4;
          background: hsla(0, 0%, 100%, 0.3);
          color: #fff;
          @include tablet {
            width: 256px;
          }
        }
      }
    }

    &--home {
      @include tablet {
        padding-left: $spacing-6;
        padding-right: $spacing-6;
      }
      @include big-tablet {
        padding-left: $spacing-8;
        padding-right: $spacing-8;
      }
      @include desktop {
        padding-left: $spacing-10;
        padding-right: $spacing-10;
      }
    }
  }
</style>
